import React, { useState } from 'react';
import { X, Calendar, FileText, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { otherCrmCreateNote } from '../../../../services/otherCRMService';

export default function CreateNoteModalExternal({ open, onClose, onCreated, entityId }) {
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      Swal.fire('Fecha requerida', 'Debes seleccionar una fecha para la nota', 'info');
      return;
    }
    if (!title || !title.trim()) {
      Swal.fire('Título requerido', 'Debes ingresar un título para la nota', 'info');
      return;
    }

    if (!entityId && entityId !== 0) {
      Swal.fire('Error', 'Falta el ID de la entidad', 'error');
      return;
    }

    try {
      setBusy(true);
      const created = await otherCrmCreateNote({ entityId, date, title: title.trim(), body: body.trim() || undefined });
      const note = created?.data || created;
      onCreated?.(note);
      Swal.fire({
        icon: 'success',
        title: 'Nota creada',
        timer: 1200,
        showConfirmButton: false,
      });
      // Reset form
      setDate('');
      setTitle('');
      setBody('');
      onClose();
    } catch (err) {
      Swal.fire('Error', err?.message || 'No se pudo crear la nota', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-auto">
        <div className="w-full max-w-2xl my-4 rounded-2xl bg-white border shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between flex-none">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-white/10 border border-white/20">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-semibold">Crear nota</div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0" title="Cerrar">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const today = new Date().toISOString().split('T')[0];
                      if (selectedDate > today) {
                        Swal.fire('Fecha inválida', 'No se permiten fechas futuras. Por favor, selecciona una fecha válida.', 'warning');
                        return;
                      }
                      setDate(selectedDate);
                    }}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    title="Fecha asociada a la nota (no se permiten fechas futuras)"
                    className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                  />
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Fecha relevante para la nota (no se permiten fechas futuras)
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej: Recordatorio importante, Seguimiento de proyecto..."
                  title="Título breve y descriptivo de la nota"
                  required
                  className="w-full h-11 px-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contenido <span className="text-gray-400 text-xs">(opcional)</span></label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Escribe aquí el contenido detallado de la nota, recordatorios, observaciones, etc."
                  title="Contenido completo de la nota: detalles, recordatorios, observaciones importantes"
                  rows={6}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button 
                  type="button"
                  onClick={onClose} 
                  className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] transition-colors text-sm font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busy || !date || !title || !title.trim()}
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Crear nota
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

